import { useCallback, useEffect, useRef } from "react"
import { usePreferences } from "hooks/use-preferences"
import { useModelPreferencesValidation } from "./preference-validation/use-model-preferences-validation"
import { modelSlice } from "../store/model-slice"
import type { ModelState } from "../store/model-slice"
import { useDashboardId } from "../context/dashboard-id"
import { useAppDispatch, useAppSelector } from "hooks/redux"

const PREFERENCE_SUBTYPE = "model"
const PREFERENCE_FIELDS = ["modelId"]

/**
 * Stable fallback — avoids allocating a new object on every render,
 * which would cause useAppSelector to detect a change every call and
 * trigger an infinite re-render loop.
 */
const EMPTY_MODEL_STATE: ModelState = { modelId: null, isPreferencesLoaded: false }

export function useSyncModelPreferences(): boolean {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  const state = useAppSelector((s) => s.dashboard.model[dashboardId] ?? EMPTY_MODEL_STATE)
  const setPreferenceValidationOptions = useModelPreferencesValidation()

  // Guard against dashboardId changing mid-lifecycle. usePreferences' load
  // effect uses isInitialLoadRef which never resets, so a new dashboardId
  // would not trigger a fresh preference load, causing the new dashboard to
  // display default state instead of the user's saved model.
  const initialDashboardIdRef = useRef(dashboardId)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && dashboardId !== initialDashboardIdRef.current) {
      throw new Error(
        `useSyncModelPreferences: dashboardId changed from "${initialDashboardIdRef.current}" to "${dashboardId}". `
        + "dashboardId must be stable for the component lifetime. "
        + "Unmount and remount DashboardLayout when navigating between dashboards, "
        + "or implement resetKey support in usePreferences.",
      )
    }
  }, [dashboardId])

  const setPreferencesAction = useCallback(
    (prefs: { modelId?: string | null }) =>
      modelSlice.actions.setPreferences({ dashboardId, ...prefs }),
    [dashboardId],
  )

  const isPreferencesRequestLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferencesAction,
    { setPreferenceValidationOptions },
  )

  // When the user has no saved preferences, usePreferences sets its loaded flag
  // true without dispatching setPreferencesAction (preferencesToApply is {}),
  // which leaves s.dashboard.model[dashboardId] undefined. Dispatch once to
  // initialize the slice with defaults so isPreferencesLoaded flips true.
  useEffect(() => {
    if (isPreferencesRequestLoaded && !state.isPreferencesLoaded) {
      dispatch(modelSlice.actions.setPreferences({ dashboardId }))
    }
  }, [isPreferencesRequestLoaded, state.isPreferencesLoaded, dashboardId, dispatch])

  return state.isPreferencesLoaded
}
